"use client"

import * as React from "react"

/**
 * An open phone sheet (a dialog or a drawer) offers itself as the host of any picker opened from inside it. The
 * picker then draws as a screen INSIDE that sheet, not as a second sheet stacked on top (one sheet at a time),
 * and taps in it stay inside the sheet, so the sheet never reads them as a tap outside and closes.
 */
const MobileSheetHostContext = React.createContext<HTMLElement | null>(null)

export function useMobileSheetHost(): HTMLElement | null {
  return React.useContext(MobileSheetHostContext)
}

/** Marks the panel, so the host sheet can grow while it shows (`has-[[data-sheet-panel]]:` on the sheet). */
export const MOBILE_SHEET_PANEL_ATTRIBUTE = "data-sheet-panel"

/**
 * While a picker shows inside it, a sheet grows only as far as the list needs: tall for a long or searched list,
 * a little for a short one, never a tall box around six rows. Applied on the sheet's own content element.
 */
export const MOBILE_SHEET_HOST_GROW_CLASS =
  "has-[[data-sheet-panel=tall]]:min-h-[min(85dvh,40rem)] has-[[data-sheet-panel=short]]:min-h-[min(60dvh,26rem)]"

export function MobileSheetHost({ children }: { children: React.ReactNode }) {
  const [element, setElement] = React.useState<HTMLDivElement | null>(null)
  return (
    <MobileSheetHostContext.Provider value={element}>
      {children}
      <div ref={setElement} data-sheet-host='' className='pointer-events-none absolute inset-0 z-40 empty:hidden' />
    </MobileSheetHostContext.Provider>
  )
}
